import React from 'react';
import { Link } from 'react-router-dom';
import { Rechnung, VorgemerkteRechnung } from '../types';
import { Badge, Button } from './ui';
import { RECHNUNG_STATUS_LABEL, RECHNUNG_STATUS_COLOR, UEBERFAELLIG_COLOR, fmtDatum } from '../config/rechnungStatus';
import { formatBetrag } from '../utils/preis';
import { ankunft } from '../utils/ankunft';

/**
 * Rechnungen als Kontoauszug: links eine senkrechte Zeitachse mit Tag und Punkt, rechts daneben
 * jede Rechnung als eigene Karte. Oben die vorgemerkten (Zukunft, gestrichelt und blass), dann
 * „Heute“, darunter die echten Rechnungen, neueste zuerst, nach Monaten gruppiert.
 *
 * Tag, Punkt, Monat und „Heute“ liegen auf dem Hintergrund, nur die Rechnungen sind Karten – so
 * bleibt die Achse als durchgehende Linie lesbar und jede Rechnung klar für sich. Der
 * „Bezahlt“-Knopf steht unter dem Betrag, damit es auf dem iPad im Hochformat ohne eigene Spalte passt.
 */

type Eintrag =
  | { art: 'vorgemerkt'; datum: string; v: VorgemerkteRechnung }
  | { art: 'heute'; datum: string }
  | { art: 'rechnung'; datum: string; r: Rechnung };

const heuteIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const monatTitel = (iso: string) =>
  new Date(`${iso.slice(0, 7)}-01T12:00:00`).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

const wochentag = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '');

/** Das Datum, an dem eine Rechnung auf der Achse steht: gestellt am …, ein Entwurf am Tag seiner Anlage. */
const datumVon = (r: Rechnung) => r.rechnungsdatum ?? r.createdAt?.slice(0, 10) ?? heuteIso();

// Spalten: Tag | Achse | Karte. Die Achse liegt mittig in Spalte 2 (siehe ACHSE_LINKS).
const ZEILE = 'grid grid-cols-[2.5rem_1rem_minmax(0,1fr)] items-start gap-x-3';
// 2.5rem Tag + 0.75rem Abstand + halbe Achsenspalte.
const ACHSE_LINKS = 'left-[calc(2.5rem+0.75rem+0.5rem)]';
// Innenleben einer Karte: Inhalt | Betrag. Punkt und Tag sitzen auf Höhe der ersten Kartenzeile.
const KARTE = 'grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 rounded-xl px-4 py-3';

/** Das Stück Zeitachse hinter einer Zeile – gestrichelt in der Zukunft, durchgezogen ab heute. */
const Achse: React.FC<{ zukunft?: boolean }> = ({ zukunft }) => (
  <span aria-hidden className={`pointer-events-none absolute inset-y-0 ${ACHSE_LINKS} border-l ${
    zukunft ? 'border-dashed border-gray-300' : 'border-gray-200'}`} />
);

const Tag: React.FC<{ iso: string; blass?: boolean; wiederholt?: boolean }> = ({ iso, blass, wiederholt }) => (
  // Wie im Kontoauszug steht der Tag nur beim ersten Eintrag des Tages.
  wiederholt ? <span /> : <span className={`pt-3 text-right leading-tight ${blass ? 'text-gray-400' : 'text-gray-700'}`}>
    <span className="block text-lg font-semibold tabular-nums">{Number(iso.slice(8, 10))}.</span>
    <span className="block text-xs text-gray-400">{wochentag(iso)}</span>
  </span>
);

// Bezahlt ist erledigt und wird grün – nach „Bezahlt“ wechselt der Punkt dann weich die Farbe.
// Der Ring hat die Farbe des Untergrunds und schneidet die Achse um den Punkt frei.
const Punkt: React.FC<{ art: 'hohl' | 'voll' | 'bezahlt' | 'rot' | 'heute'; ring: string; oben?: string }> =
  ({ art, ring, oben = 'pt-[1.2rem]' }) => (
    <span className={`relative z-10 flex justify-center ${oben}`}>
      <span className={`block rounded-full ring-4 transition-colors duration-500 ${
        art === 'hohl' ? `h-3 w-3 border-2 border-gray-300 bg-gray-50 ${ring}`
          : art === 'rot' ? `h-3 w-3 bg-red-500 ${ring}`
            : art === 'heute' ? 'h-3.5 w-3.5 bg-brand-600 ring-brand-100'
              : art === 'bezahlt' ? `h-3 w-3 bg-brand-500 ${ring}`
                : `h-3 w-3 bg-gray-400 ${ring}`}`} />
    </span>
  );

const Betrag: React.FC<{ wert: number; blass?: boolean }> = ({ wert, blass }) => (
  <span className={`block whitespace-nowrap text-right text-base font-semibold tabular-nums ${
    blass ? 'text-gray-400' : wert < 0 ? 'text-red-700' : 'text-gray-900'}`}>
    {formatBetrag(wert)}
  </span>
);

interface Darstellung {
  ohneKunde?: boolean;
  /** Innerhalb einer weißen Karte (Kundenseite) statt auf dem grauen Seitenhintergrund. */
  eingebettet?: boolean;
  onBezahlt?: (r: Rechnung) => void;
  bezahltLaeuft?: number | null;
}

const RechnungsZeitachse: React.FC<Darstellung & {
  rechnungen: Rechnung[];
  vorgemerkte?: VorgemerkteRechnung[];
  /** „Heute“ markieren – nur sinnvoll, wenn es darüber eine Zukunft gibt. */
  mitHeute?: boolean;
  /** Kopf über den vorgemerkten Einträgen (Summe, Hinweis, Laden). */
  kopf?: React.ReactNode;
}> = ({ rechnungen, vorgemerkte = [], mitHeute, kopf, ...darstellung }) => {
  const heute = heuteIso();
  const eintraege: Eintrag[] = [
    ...vorgemerkte.map(v => ({ art: 'vorgemerkt' as const, datum: v.erstelltAm, v })),
    ...(mitHeute ? [{ art: 'heute' as const, datum: heute }] : []),
    ...rechnungen.map(r => ({ art: 'rechnung' as const, datum: datumVon(r), r })),
  ];

  // Monatsüberschrift, sobald der Monat wechselt. Der Heute-Marker zählt zu seinem Monat.
  const zeilen: React.ReactNode[] = [];
  let monat = '';
  let tag = '';
  eintraege.forEach((e, i) => {
    const wiederholt = e.art !== 'heute' && e.datum === tag;
    if (e.art !== 'heute') tag = e.datum;
    const m = e.datum.slice(0, 7);
    if (m !== monat) {
      monat = m;
      zeilen.push(
        <li key={`m-${m}-${i}`} className={`${ZEILE} relative pb-2 pt-5 first:pt-1`}>
          <Achse zukunft={e.art === 'vorgemerkt'} />
          <span />
          <span />
          <h3 className="text-sm font-semibold text-gray-500">{monatTitel(e.datum)}</h3>
        </li>,
      );
    }
    zeilen.push(<Zeile key={schluessel(e)} e={e} wiederholt={wiederholt} {...darstellung} />);
  });

  return (
    <div>
      {kopf}
      <ol>{zeilen}</ol>
    </div>
  );
};

const schluessel = (e: Eintrag) =>
  e.art === 'vorgemerkt' ? `v-${e.v.vertragId}-${e.v.abrechnungsjahr}` : e.art === 'heute' ? 'heute' : `r-${e.r.id}`;

const Zeile: React.FC<Darstellung & { e: Eintrag; wiederholt?: boolean }> =
  ({ e, wiederholt, ohneKunde, eingebettet, onBezahlt, bezahltLaeuft }) => {
    const ring = eingebettet ? 'ring-white' : 'ring-slate-50';

    if (e.art === 'heute') {
      return (
        <li className={`${ZEILE} relative py-3`}>
          <Achse />
          <span />
          <Punkt art="heute" ring={ring} oben="pt-1" />
          <span className="text-sm font-semibold text-brand-700">
            Heute, {new Date().toLocaleDateString('de-DE', { day: 'numeric', month: 'long' })}
          </span>
        </li>
      );
    }

    if (e.art === 'vorgemerkt') {
      const v = e.v;
      return (
        <li className={`${ZEILE} relative py-1`}>
          <Achse zukunft />
          <Tag iso={v.erstelltAm} blass wiederholt={wiederholt} />
          <Punkt art="hohl" ring={ring} />
          <Link
            to={`/vertraege/${v.vertragId}`}
            title="Noch keine Rechnung: Sie entsteht an diesem Tag als Entwurf. Tippen öffnet den Vertrag."
            className={`${KARTE} border border-dashed border-gray-300 ${eingebettet ? 'bg-gray-50/50' : 'bg-white/50'} transition-colors hover:bg-white active:bg-gray-100`}
          >
            <span className="min-w-0">
              <span className="block truncate font-medium text-gray-500">
                {ohneKunde ? `Vertragsjahr ${v.abrechnungsjahr}` : v.kundeName || 'Unbekannter Kunde'}
                {/* Sichtbar sagen das gestrichelte Blatt und der hohle Punkt; für Screenreader das Wort. */}
                <span className="sr-only"> (vorgemerkt)</span>
              </span>
              <span className="mt-0.5 block text-sm text-gray-400">
                {v.vertragsnummer ?? 'Vertrag'}, {v.positionen.length === 1 ? '1 Position' : `${v.positionen.length} Positionen`}
              </span>
            </span>
            <Betrag wert={v.summeBrutto} blass />
          </Link>
        </li>
      );
    }

    const r = e.r;
    const titel = ohneKunde ? (r.rechnungsnummer ?? 'Entwurf') : r.kundeName || 'Unbekannter Kunde';
    const unterzeile = [
      ohneKunde ? null : r.rechnungsnummer ?? 'noch ohne Nummer',
      `Vertragsjahr ${r.abrechnungsjahr}`,
      r.faelligAm && r.status === 'GESTELLT' ? `fällig ${fmtDatum(r.faelligAm)}` : null,
      r.storniertRechnungsnummer ? `Storno zu ${r.storniertRechnungsnummer}` : null,
    ].filter((t): t is string => Boolean(t));

    const a = ankunft(`rechnung-${r.id}`);
    return (
      <li className={`${ZEILE} relative py-1`}>
        <Achse />
        <Tag iso={e.datum} wiederholt={wiederholt} />
        <Punkt art={r.ueberfaellig ? 'rot' : r.status === 'BEZAHLT' ? 'bezahlt' : 'voll'} ring={ring} />
        <div
          ref={a.ref}
          className={`${KARTE} relative border bg-white transition-colors ${
            eingebettet ? 'border-gray-200' : 'border-gray-200 shadow-sm'} hover:border-gray-300 ${a.className}`}
        >
          <Link to={`/rechnungen/${r.id}`} className="min-w-0 after:absolute after:inset-0 after:rounded-xl">
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="truncate font-medium text-gray-900">{titel}</span>
              {/* key = Status: bei einem Wechsel wird das Badge neu gemountet und blendet um – aber nur,
                  wenn die Rechnung gerade markiert ist, sonst ploppten beim Laden alle Badges auf. */}
              {r.ueberfaellig
                ? <span key="ueberfaellig" className={`inline-flex ${a.className && 'ui-wechsel'}`}><Badge className={UEBERFAELLIG_COLOR}>Überfällig</Badge></span>
                : <span key={r.status} className={`inline-flex ${a.className && 'ui-wechsel'}`}><Badge className={RECHNUNG_STATUS_COLOR[r.status]}>{RECHNUNG_STATUS_LABEL[r.status]}</Badge></span>}
            </span>
            <span className="mt-0.5 block text-sm text-gray-500">
              {unterzeile.map((t, i) => (
                <React.Fragment key={t}>{i > 0 && ', '}<span className="whitespace-nowrap">{t}</span></React.Fragment>
              ))}
            </span>
          </Link>
          <span className="flex flex-col items-end gap-2">
            <Betrag wert={r.summeBrutto} />
            {onBezahlt && r.status === 'GESTELLT' && (
              <span className="relative z-10">
                <Button size="sm" variant="secondary" loading={bezahltLaeuft === r.id} onClick={() => onBezahlt(r)}>
                  Bezahlt
                </Button>
              </span>
            )}
          </span>
        </div>
      </li>
    );
  };

export default RechnungsZeitachse;
